type Environment = "staging" | "production" | "uat";

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}

interface CloudRunServices {
  aiSearch: string;
  aiPropertyDetails: string;
  searchAutocomplete: string;
  valuationPhotos: string;
  tenantConfig: string;
  webActivity: string;
  ealertSystem: string;
  graphqlUrl: string;
  // Consolidated MLS service (was the mls-logo-compliance function). Serves
  // /access (logo + compliance read). Prod points at the legacy function
  // until `mls` is deployed to heyisaai.
  mls: string;
}

interface EnvironmentConfig {
  firebase: FirebaseConfig;
  cloudFunctions: {
    baseURL: string;
  };
  cloudRunServices: CloudRunServices;
}

const stagingConfig: EnvironmentConfig = {
  firebase: {
    apiKey: "AIzaSyCCTovTXros2NLNbON26jpfkcX3fyxngL0",
    authDomain: "speculo-staging.firebaseapp.com",
    projectId: "speculo-staging",
    storageBucket: "speculo-staging.firebasestorage.app",
    messagingSenderId: "70707136872",
    appId: "1:70707136872:web:03165f1c55f1ad00b0eedd",
    measurementId: "G-PF7K09MR13",
  },
  cloudFunctions: {
    baseURL: "https://us-central1-speculo-staging.cloudfunctions.net",
  },
  cloudRunServices: {
    aiSearch: "https://ai-search-70707136872.us-central1.run.app",
    aiPropertyDetails:
      "https://ai-property-details-70707136872.us-central1.run.app",
    searchAutocomplete:
      "https://search-autocomplete-i6gut2dxyq-uc.a.run.app",
    valuationPhotos: "https://valuation-photos-oon2jldm5a-uc.a.run.app",
    tenantConfig: "https://tenant-config-70707136872.us-central1.run.app",
    webActivity: "https://webactivity-70707136872.us-central1.run.app",
    ealertSystem: "https://ealert-system-54875993561.us-central1.run.app",
    graphqlUrl: "https://remi-api-70707136872.us-central1.run.app/graphql",
    mls: "https://mls-i6gut2dxyq-uc.a.run.app",
  },
};

const uatConfig: EnvironmentConfig = {
  firebase: {
    apiKey: "AIzaSyAoRc4W7QJKpWOBg-Wvs23VyLf-EfzJOj8",
    authDomain: "speculo-uat.firebaseapp.com",
    projectId: "speculo-uat",
    storageBucket: "speculo-uat.firebasestorage.app",
    messagingSenderId: "845225729852",
    appId: "1:845225729852:web:45649415080bdcd4d1c839",
    measurementId: "G-F0BEW1KEZ6",
  },
  cloudFunctions: {
    baseURL: "https://us-central1-speculo-uat.cloudfunctions.net",
  },
  cloudRunServices: {
    aiSearch: "https://ai-search-845225729852.us-central1.run.app",
    aiPropertyDetails:
      "https://ai-property-details-845225729852.us-central1.run.app",
    searchAutocomplete:
      "https://search-autocomplete-845225729852.us-central1.run.app",
    valuationPhotos: "https://valuation-photos-oon2jldm5a-uc.a.run.app",
    tenantConfig: "https://tenant-config-845225729852.us-central1.run.app",
    webActivity: "https://webactivity-845225729852.us-central1.run.app",
    ealertSystem: "https://ealert-system-845225729852.us-central1.run.app",
    graphqlUrl: "https://remi-api-845225729852.us-central1.run.app/graphql",
    mls: "https://mls-afdf425mta-uc.a.run.app",
  },
};

const productionConfig: EnvironmentConfig = {
  firebase: {
    apiKey: "AIzaSyDVuZ1z3umUXPErCuEYox3se0LbHhEYjhI",
    authDomain: "heyisaai.firebaseapp.com",
    projectId: "heyisaai",
    storageBucket: "heyisaai.appspot.com",
    messagingSenderId: "54875993561",
    appId: "1:54875993561:web:55d04f09a41667f99495c8",
    measurementId: "G-51HE24K2Q5",
  },
  cloudFunctions: {
    baseURL: "https://us-central1-heyisaai.cloudfunctions.net",
  },
  cloudRunServices: {
    aiSearch: "https://ai-search-54875993561.us-central1.run.app",
    aiPropertyDetails:
      "https://ai-property-details-54875993561.us-central1.run.app",
    searchAutocomplete:
      "https://search-autocomplete-54875993561.us-central1.run.app",
    valuationPhotos: "https://valuation-photos-oon2jldm5a-uc.a.run.app",
    tenantConfig: "https://tenant-config-oon2jldm5a-uc.a.run.app",
    webActivity: "https://webactivity-54875993561.us-central1.run.app",
    ealertSystem: "https://ealert-system-54875993561.us-central1.run.app",
    graphqlUrl: "https://remi-api-54875993561.us-central1.run.app/graphql",
    // ponytail: mls service not yet deployed to heyisaai; stay on the legacy
    // function so `${mls}/access` resolves to the working prod endpoint.
    // Switch to the mls run.app URL once it's live in prod.
    mls: "https://us-central1-heyisaai.cloudfunctions.net/mls-logo-compliance",
  },
};

// Mirrors speculo-app/src/config/environment.ts: default to staging when
// VITE_APP_ENV is unset. Every prod-target npm script (dev:prod, build:prod,
// deploy) sets VITE_APP_ENV=production explicitly, so the default is never
// load-bearing for shipping to prod.
const currentEnvironment: Environment = (import.meta.env.VITE_APP_ENV ||
  "staging") as Environment;

export const getConfig = (): EnvironmentConfig => {
  switch (currentEnvironment) {
    case "production":
      return productionConfig;
    case "uat":
      return uatConfig;
    default:
      return stagingConfig;
  }
};

export const isProduction = () => currentEnvironment === "production";
export const isStaging = () => currentEnvironment === "staging";
export const isUat = () => currentEnvironment === "uat";
